const priorityCache = new Map<string, PriorityAssessment>();

function createCacheKey(inventory: ActionInventory): string {
  return JSON.stringify({
    action: inventory.action,
    frequency: inventory.frequency,
    currentMethod: inventory.currentMethod,
    timeTaken: inventory.timeTaken,
    userType: inventory.userType,
    isConvention: inventory.isConvention,
    requiresModeSwitching: inventory.requiresModeSwitching,
    isDestructive: inventory.isDestructive,
    requiresConsideration: inventory.requiresConsideration,
  });
}

export interface ActionInventory {
  action: string;
  frequency: number;
  currentMethod: "mouse" | "menu" | "keyboard" | "click";
  timeTaken: number;
  userType: "all" | "power" | "occasional";
  isConvention?: boolean;
  requiresModeSwitching?: boolean;
  isDestructive?: boolean;
  requiresConsideration?: boolean;
}

export interface PriorityAssessment {
  priority: number;
  category: "critical" | "high" | "medium" | "low" | "skip";
  recommendation: "implement" | "consider" | "skip";
  reasoning: string[];
  suggestedModifiers: string[];
}

export function calculatePriority(inventory: ActionInventory): PriorityAssessment {
  const cacheKey = createCacheKey(inventory);
  const cached = priorityCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  let score = 0;
  const reasoning: string[] = [];

  //* Frequency scoring: 5 points per use, capped at 50 (10+ uses per session)
  const frequencyScore = Math.min(inventory.frequency * 5, 50);
  score += frequencyScore;

  if (inventory.frequency > 10) {
    reasoning.push(`High frequency (${inventory.frequency}x per session)`);
  } else if (inventory.frequency > 3) {
    reasoning.push(`Medium frequency (${inventory.frequency}x per session)`);
  } else if (inventory.frequency > 0) {
    reasoning.push(`Low frequency (${inventory.frequency}x per session)`);
  }

  //* Time savings: 4 points per second saved, capped at 20 (5+ seconds)
  const timeScore = Math.min(inventory.timeTaken * 4, 20);
  score += timeScore;

  if (inventory.timeTaken > 3) {
    reasoning.push(`Significant time savings (${inventory.timeTaken}s per action)`);
  }

  //* User type weighting: all users (20 pts), power users (12 pts), occasional (5 pts)
  const userTypeScores = {
    all: 20,
    power: 12,
    occasional: 5,
  };
  score += userTypeScores[inventory.userType];

  if (inventory.userType === "all") {
    reasoning.push("Relevant to all users");
  } else if (inventory.userType === "power") {
    reasoning.push("Primarily for power users");
  }

  if (inventory.isConvention) {
    score += 15;
    reasoning.push("Standard keyboard convention (muscle memory exists)");
  }

  if (inventory.requiresModeSwitching) {
    score += 10;
    reasoning.push("Reduces keyboard ↔ mouse switching");
  }

  if (inventory.isDestructive) {
    score -= 30;
    reasoning.push("⚠️ Destructive action - penalty applied");
  }

  if (inventory.requiresConsideration) {
    score -= 20;
    reasoning.push("⚠️ Requires careful consideration - penalty applied");
  }

  if (inventory.currentMethod === "keyboard") {
    score -= 10;
    reasoning.push("Already has keyboard access");
  }

  score = Math.max(0, Math.min(100, score));

  //* Priority bands: 80+ critical, 60-79 high, 40-59 medium, 20-39 low, <20 skip
  let category: PriorityAssessment["category"];
  let recommendation: PriorityAssessment["recommendation"];

  if (score >= 80) {
    category = "critical";
    recommendation = "implement";
    reasoning.push("✅ CRITICAL - Must implement");
  } else if (score >= 60) {
    category = "high";
    recommendation = "implement";
    reasoning.push("✅ HIGH - Should implement");
  } else if (score >= 40) {
    category = "medium";
    recommendation = "consider";
    reasoning.push("⚡ MEDIUM - Consider implementing");
  } else if (score >= 20) {
    category = "low";
    recommendation = "skip";
    reasoning.push("⏸️ LOW - Low priority");
  } else {
    category = "skip";
    recommendation = "skip";
    reasoning.push("❌ SKIP - Not worth implementing");
  }

  const suggestedModifiers = suggestModifiers(inventory, score);

  const result: PriorityAssessment = {
    priority: score,
    category,
    recommendation,
    reasoning,
    suggestedModifiers,
  };

  priorityCache.set(cacheKey, result);

  return result;
}

function suggestModifiers(inventory: ActionInventory, priority: number): string[] {
  if (inventory.isDestructive || inventory.requiresConsideration) {
    return ["ctrl", "shift"];
  }

  if (inventory.isConvention) {
    const actionLower = inventory.action.toLowerCase();

    const conventionModifiers: Record<string, string[]> = {
      save: ["ctrl"],
      copy: ["ctrl"],
      paste: ["ctrl"],
      cut: ["ctrl"],
      undo: ["ctrl"],
      redo: ["ctrl"],
      find: ["ctrl"],
      new: ["ctrl"],
      open: ["ctrl"],
      print: ["ctrl"],
    };

    for (const [keyword, modifiers] of Object.entries(conventionModifiers)) {
      if (actionLower.includes(keyword)) {
        return modifiers;
      }
    }
  }

  if (priority >= 60) {
    return ["ctrl"];
  }

  if (priority >= 40) {
    return ["ctrl", "shift"];
  }

  return ["ctrl", "shift"];
}

export function assessActions(actions: ActionInventory[]): Map<string, PriorityAssessment> {
  const assessments = new Map<string, PriorityAssessment>();

  for (const action of actions) {
    const assessment = calculatePriority(action);
    assessments.set(action.action, assessment);
  }

  return assessments;
}

export function generatePriorityReport(actions: ActionInventory[]): string {
  const assessments = assessActions(actions);
  const lines: string[] = [];

  lines.push("# Hotkey Priority Assessment Report\n");
  lines.push(`Total actions assessed: ${actions.length}\n`);

  const byCategory = new Map<string, Array<[string, PriorityAssessment]>>();
  const categoryOrder = ["critical", "high", "medium", "low", "skip"];

  categoryOrder.forEach((cat) => byCategory.set(cat, []));

  for (const [action, assessment] of assessments) {
    byCategory.get(assessment.category)!.push([action, assessment]);
  }

  for (const items of byCategory.values()) {
    items.sort((a, b) => b[1].priority - a[1].priority);
  }

  for (const category of categoryOrder) {
    const items = byCategory.get(category)!;
    if (items.length === 0) continue;

    lines.push(`\n## ${category.toUpperCase()} Priority (${items.length} actions)\n`);

    for (const [action, assessment] of items) {
      lines.push(
        `### ${action}`,
        `- **Score:** ${assessment.priority}/100`,
        `- **Recommendation:** ${assessment.recommendation}`,
        `- **Suggested modifiers:** ${assessment.suggestedModifiers.join("+")}`,
        "- **Reasoning:**",
        ...assessment.reasoning.map((r) => `  - ${r}`),
        "",
      );
    }
  }

  lines.push("\n## Summary\n");
  lines.push("| Category | Count | Action |");
  lines.push("|----------|-------|--------|");

  for (const category of categoryOrder) {
    const count = byCategory.get(category)!.length;
    let action: string;
    if (category === "critical" || category === "high") {
      action = "✅ Implement";
    } else if (category === "medium") {
      action = "⚡ Consider";
    } else {
      action = "❌ Skip";
    }
    lines.push(`| ${category} | ${count} | ${action} |`);
  }

  return lines.join("\n");
}

export const COMMON_ACTIONS: ActionInventory[] = [
  {
    action: "Save",
    frequency: 15,
    currentMethod: "click",
    timeTaken: 2,
    userType: "all",
    isConvention: true,
  },
  {
    action: "Copy",
    frequency: 20,
    currentMethod: "click",
    timeTaken: 1,
    userType: "all",
    isConvention: true,
  },
  {
    action: "Paste",
    frequency: 20,
    currentMethod: "click",
    timeTaken: 1,
    userType: "all",
    isConvention: true,
  },

  {
    action: "Search",
    frequency: 8,
    currentMethod: "click",
    timeTaken: 3,
    userType: "all",
    requiresModeSwitching: true,
  },
  {
    action: "Close modal",
    frequency: 10,
    currentMethod: "click",
    timeTaken: 1,
    userType: "all",
    isConvention: true,
  },

  {
    action: "Archive email",
    frequency: 5,
    currentMethod: "click",
    timeTaken: 1.5,
    userType: "power",
  },
  {
    action: "Next item",
    frequency: 12,
    currentMethod: "click",
    timeTaken: 0.5,
    userType: "power",
  },

  {
    action: "Change theme",
    frequency: 0.1,
    currentMethod: "click",
    timeTaken: 5,
    userType: "occasional",
  },
  {
    action: "Delete account",
    frequency: 0.01,
    currentMethod: "click",
    timeTaken: 10,
    userType: "occasional",
    isDestructive: true,
    requiresConsideration: true,
  },
];

export function clearPriorityCache(): void {
  priorityCache.clear();
}

export function getPriorityCacheStats(): { size: number; maxSize?: number } {
  return {
    size: priorityCache.size,
    maxSize: 1000, // TODO: Consider implementing LRU cache if size approaches this limit
  };
}
